import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useRoles } from '@/hooks/useRoles';
import { Chip } from '@/components/ui/Chip';
import { useActiveEvent } from '@/hooks/useActiveEvent';
import { EventSelector } from '@/components/events/EventSelector';
import { HelpButton } from '@/components/tour/HelpButton';
import { useEffect } from 'react';
import { useEvent } from '@/hooks/useEvents';

export function TopBar() {
  const { user } = useAuth();
  const { roleId } = usePermissions();
  const { data: roles } = useRoles();
  const roleName = roles?.find((r) => r.id === roleId)?.name;
  const { activeEvent, setActiveEvent, clear } = useActiveEvent();
  // The choice is remembered per device; forget it once its event is gone, or
  // the selector keeps pointing at nothing.
  const { data: activeEventRow, isSuccess: activeEventLoaded } = useEvent(activeEvent?.id ?? null);
  useEffect(() => {
    if (activeEvent && activeEventLoaded && !activeEventRow) clear();
  }, [activeEvent, activeEventLoaded, activeEventRow, clear]);

  if (!user) return null;

  return (
    <header className="h-16 bg-surface/80 backdrop-blur-md border-b border-line sticky top-0 z-20">
      <div className="h-full px-6 flex items-center justify-between gap-4">
        <div className="md:hidden flex items-center gap-2">
          <div className="w-7 h-7 rounded bg-primary flex items-center justify-center text-white font-display font-semibold text-small">
            O
          </div>
          <span className="font-display font-semibold text-ink">Ophth POS</span>
        </div>

        <div data-tour="active-event" className="flex-1 hidden md:flex items-center gap-3">
          <span className="text-small text-ink-subtle">Active event:</span>
          <EventSelector value={activeEvent?.id ?? null} onChange={setActiveEvent} />
          {activeEvent && (
            <Chip variant="success" className="ml-2">
              <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
              Live
            </Chip>
          )}
        </div>

        <div className="flex items-center gap-3">
          <HelpButton />
          {roleName && (
            <Chip variant="info" className="hidden sm:inline-flex">
              {roleName}
            </Chip>
          )}
          <div className="md:hidden flex items-center gap-2 text-small">
            <span className="text-ink truncate max-w-[140px]">{user.displayName}</span>
            <span className="chip capitalize">{user.role}</span>
          </div>
        </div>
      </div>
    </header>
  );
}
